import {
  PropsWithChildren,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import type {
  Auth0ProviderWithConfigOptions,
  GetTokenSilentlyOptions,
} from '@auth0/auth0-react';
import {
  AppState,
  Auth0Context,
  Auth0ContextInterface,
  Auth0Provider,
  User,
  initialContext,
  useAuth0,
} from '@auth0/auth0-react';

import { Region } from '../../../utils';
import { useNavigate } from '../../routing';
import {
  OAUTH_CALLBACK_PARAMS,
  SILENT_AUTH_RELOGIN_ERRORS,
  getAuth0ErrorCode,
  hasOAuthCallbackParams,
  isAuthCallbackReplayError,
} from './helpers';
import {
  getExpirationEpochFromToken,
  getOrgNameFromToken,
  getRegionFromTokenOrDefault,
} from './jwt';
import { useOrgId } from './useOrgId';

export type AuthOptions = Auth0ProviderWithConfigOptions;
export type TokenOptions = GetTokenSilentlyOptions;
export type AuthenticationMode = 'application' | 'signup';

export const createAuthOptions = (opts: AuthOptions) => opts;
export const createTokenOptions = (opts: TokenOptions) => opts;

export const DOMAIN = 'auth-eu.technology.byspotify.com';
export const CLIENT_ID = '2fG3H4RhlAbIZm9Rfn32zTaILH7w1X4w';
export const AUDIENCE = 'https://confidence.dev/';

export const getCurrentLocationPath = (omitParams: string[] = []): string => {
  const params = new URLSearchParams(window.location.search);
  const paramsToOmit = hasOAuthCallbackParams(params)
    ? [...OAUTH_CALLBACK_PARAMS, ...omitParams]
    : omitParams;
  paramsToOmit.forEach(param => params.delete(param));
  const search = params.toString();

  return `${window.location.pathname}${search ? `?${search}` : ''}${
    window.location.hash
  }`;
};

export { getAuth0ErrorCode, hasOAuthCallbackParams } from './helpers';

export const CONFIDENCE_TokenOptions = createTokenOptions({
  authorizationParams: {
    audience: AUDIENCE,
    scope: 'openid profile email offline_access',
  },
});

export const CONFIDENCE_DefaultAuthOptions: Omit<AuthOptions, 'clientId'> = {
  domain: DOMAIN,
  cacheLocation: 'localstorage',
  useRefreshTokens: true,
  useRefreshTokensFallback: true, // Temporary, remove once affected users have logged in again
  ...CONFIDENCE_TokenOptions,
};

export const CONFIDENCE_SignupAuthOptions: Omit<AuthOptions, 'clientId'> = {
  ...CONFIDENCE_DefaultAuthOptions,
  cacheLocation: 'memory',
  useRefreshTokensFallback: false,
  useCookiesForTransactions: true,
};

const explicitOrganizationFromSearchParams = (
  searchParams: URLSearchParams,
): string | null =>
  searchParams.get('organization') || searchParams.get('account');

export const resolveAuthOrganization = ({
  mode,
  explicitOrganization,
  storedOrganization,
}: {
  mode: AuthenticationMode;
  explicitOrganization?: string | null;
  storedOrganization?: string;
}): string | undefined =>
  explicitOrganization ||
  (mode === 'application' ? storedOrganization : undefined) ||
  undefined;

const ATTEMPTED_ORG_STORAGE_KEY = 'attemptedOrgId';

const rememberAttemptedOrganization = (organization: string | undefined) => {
  if (organization) {
    sessionStorage.setItem(ATTEMPTED_ORG_STORAGE_KEY, organization);
    return;
  }

  sessionStorage.removeItem(ATTEMPTED_ORG_STORAGE_KEY);
};

const storedOrganizationWasRejected = ({
  storedOrganization,
  requestedOrganization,
}: {
  storedOrganization: string | undefined;
  requestedOrganization: string | undefined;
}): boolean =>
  Boolean(
    storedOrganization &&
      requestedOrganization &&
      storedOrganization === requestedOrganization,
  );

// Hour of day when, if possible, to refresh the access token
const DESIRED_TOKEN_REFRESH_HOUR = 3;

const TokenClaimsContext = createContext<{ orgName?: string }>({});
const SignupAuth0Context = createContext<Auth0ContextInterface>(initialContext);

type AccessTokenAcquisition = {
  error?: Error;
  retrying: boolean;
  retry: () => void;
};

const AccessTokenAcquisitionContext = createContext<AccessTokenAcquisition>({
  retrying: false,
  retry: () => {},
});

export const useAccessTokenAcquisition = (): AccessTokenAcquisition =>
  useContext(AccessTokenAcquisitionContext);

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

const TokenWrapper = ({
  context,
  onTokenReceived,
  children,
}: PropsWithChildren<{
  onTokenReceived: (token: string, region: Region) => void;
  context: Auth0ContextInterface<User>;
}>) => {
  const [token, setToken] = useState<string | null>(null);
  const [acquisitionError, setAcquisitionError] = useState<Error>();
  const [retrying, setRetrying] = useState(false);
  const tokenRef = useRef<string | null>(null);
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect, user } =
    context;
  const orgId = user?.org_id;
  const [searchParams] = useSearchParams();
  const isOAuthCallback = hasOAuthCallbackParams(searchParams);

  const getToken = useCallback(
    async (options = CONFIDENCE_TokenOptions) => {
      if (!isAuthenticated) return;
      if (isOAuthCallback) {
        return;
      }
      try {
        const tkn = await getAccessTokenSilently({
          ...options,
          authorizationParams: {
            ...options.authorizationParams,
            organization: orgId,
          },
        });

        tokenRef.current = tkn;
        setToken(tkn);
        setAcquisitionError(undefined);
        onTokenReceived(tkn, getRegionFromTokenOrDefault(tkn));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);

        const errorCode = getAuth0ErrorCode(e);
        if (
          errorCode &&
          SILENT_AUTH_RELOGIN_ERRORS.includes(errorCode) &&
          (errorCode !== 'interaction_required' || orgId)
        ) {
          rememberAttemptedOrganization(orgId);
          try {
            await loginWithRedirect({
              authorizationParams: { organization: orgId },
              appState: { returnTo: getCurrentLocationPath() },
            });
          } catch (redirectError) {
            setAcquisitionError(toError(redirectError));
          }
          return;
        }

        if (!tokenRef.current) {
          setAcquisitionError(toError(e));
        }
      }
    },
    [
      isAuthenticated,
      getAccessTokenSilently,
      loginWithRedirect,
      orgId,
      onTokenReceived,
      isOAuthCallback,
    ],
  );

  useEffect(() => {
    getToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, orgId, isOAuthCallback]);

  const retry = useCallback(async () => {
    setRetrying(true);
    try {
      await getToken();
    } finally {
      setRetrying(false);
    }
  }, [getToken]);

  const acquisition = useMemo(
    (): AccessTokenAcquisition => ({
      error: acquisitionError,
      retrying,
      retry,
    }),
    [acquisitionError, retry, retrying],
  );

  useEffect(() => {
    /*
     * The goal of this hook is to make sure token refresh happens when it's least likely a user is in the
     * middle of performing work in the app. Silent refresh can still interrupt in-flight requests if
     * Auth0 needs a full re-login.
     *
     * The way this works is by trying to figure out a time of day when the user is unlikely to be working,
     * and then refresh the token at that time. There are 7 different combinations of current time and expiry
     * time of the access token.
     */
    if (!token) {
      return undefined;
    }

    const tokenExpirationEpoch = getExpirationEpochFromToken(token);
    if (!tokenExpirationEpoch) {
      return undefined;
    }

    const tokenExpirationDate = new Date(tokenExpirationEpoch);
    const now = new Date();

    const preliminaryRefreshDate = new Date(tokenExpirationDate);
    preliminaryRefreshDate.setHours(DESIRED_TOKEN_REFRESH_HOUR);
    preliminaryRefreshDate.setMinutes(0);
    preliminaryRefreshDate.setSeconds(0);
    preliminaryRefreshDate.setMilliseconds(0);

    let refreshEpoch: number;
    if (
      preliminaryRefreshDate <= tokenExpirationDate &&
      preliminaryRefreshDate >= now
    ) {
      // Okay to refresh on the preliminaryRefreshDate since it's after now but before the token expires
      refreshEpoch = preliminaryRefreshDate.getTime();
    } else if (
      preliminaryRefreshDate > tokenExpirationDate &&
      tokenExpirationDate.getDate() !== now.getDate() &&
      now.getHours() < DESIRED_TOKEN_REFRESH_HOUR
    ) {
      // Handles an edge case where the token expires the next day, but before the desired refresh hour,
      // AND the current hour is before the desired refresh hour. In this case, we should refresh the token
      // at the desired hour the CURRENT day. If we were to take the branch below, we would refresh the token
      // the same time every day (between 00 and DESIRED_TOKEN_REFRESH_HOUR).
      const upcomingRefreshDate = new Date();
      upcomingRefreshDate.setHours(DESIRED_TOKEN_REFRESH_HOUR);
      upcomingRefreshDate.setMinutes(0);
      upcomingRefreshDate.setSeconds(0);
      upcomingRefreshDate.setMilliseconds(0);
      refreshEpoch = upcomingRefreshDate.getTime();
    } else {
      // Refresh the token immediately. This will lead to the token being refreshed at the desired hour,
      // sometimes the next day, sometimes in two days.
      refreshEpoch = 0;
    }

    /*
     * Use setInterval to check regularly if the desired refresh time has passed. If so, refresh the token.
     *
     * The reason we don't use setTimeout is that behavior varies depending on both browser and OS when the
     * computer goes to sleep or hibernate. Using setInterval and polling more frequently is more reliable.
     * The relatively high frequency of the polling is to avoid the user coming back and resuming work with
     * an expired token.
     */
    const CHECK_TIME_INTERVAL = 1000 * 10;
    const checkInterval = setInterval(() => {
      if (Date.now() > refreshEpoch) {
        getToken({
          ...CONFIDENCE_TokenOptions,
          cacheMode: 'off', // Force refresh from server
        });
        // Stop polling to avoid multiple parallel refreshes
        clearInterval(checkInterval);
      }
    }, CHECK_TIME_INTERVAL);

    return () => {
      clearInterval(checkInterval);
    };
  }, [token, getToken]);

  const tokenClaims = useMemo(
    () => ({ orgName: token ? getOrgNameFromToken(token) : undefined }),
    [token],
  );

  return (
    <AccessTokenAcquisitionContext.Provider value={acquisition}>
      <TokenClaimsContext.Provider value={tokenClaims}>
        {children}
      </TokenClaimsContext.Provider>
    </AccessTokenAcquisitionContext.Provider>
  );
};

const FailedRedirectCallbackCleanup = ({
  error,
  clearInvalidOrganization,
  clearAttemptedOrganization,
  handlesRedirectCallback,
}: {
  error?: Error;
  clearInvalidOrganization: boolean;
  clearAttemptedOrganization: boolean;
  handlesRedirectCallback: boolean;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [storedOrgId, setOrgId] = useOrgId();

  const hasFailedCallback =
    handlesRedirectCallback && !!error && hasOAuthCallbackParams(searchParams);
  const errorText = `${error?.message ?? ''} ${searchParams.get('error_description') ?? ''}`;
  const hasInvalidOrganizationError =
    clearInvalidOrganization &&
    searchParams.has('error') &&
    /organization.*invalid|invalid.*organization/i.test(errorText);
  const shouldClearStoredOrganization =
    hasInvalidOrganizationError &&
    storedOrganizationWasRejected({
      storedOrganization: storedOrgId,
      requestedOrganization:
        sessionStorage.getItem(ATTEMPTED_ORG_STORAGE_KEY) ??
        searchParams.get('organization') ??
        undefined,
    });

  useEffect(() => {
    if (!hasFailedCallback) {
      return;
    }

    if (shouldClearStoredOrganization) {
      setOrgId(null);
    }

    if (clearAttemptedOrganization) {
      sessionStorage.removeItem(ATTEMPTED_ORG_STORAGE_KEY);
    }

    const remainingParams = new URLSearchParams(searchParams);
    OAUTH_CALLBACK_PARAMS.forEach(param => remainingParams.delete(param));
    setSearchParams(remainingParams, { replace: true });
  }, [
    hasFailedCallback,
    shouldClearStoredOrganization,
    clearAttemptedOrganization,
    searchParams,
    setSearchParams,
    setOrgId,
  ]);

  return null;
};

/**
 * ! Constraint #02 - No Hardwired Config.
 * ! The Provider comes with backed-in `Confidence_AuthOptions`
 *
 * * todo(@oussaz) - passe AuthOptions as a prop or renderProp.
 */
export const AuthenticationProvider = ({
  children,
  onTokenReceived,
  clientId = CLIENT_ID,
  redirectUri,
  mode = 'application',
}: {
  children: ReactElement;
  onTokenReceived: (token: string, region: Region) => void;
  clientId?: string;
  redirectUri?: string;
  mode?: AuthenticationMode;
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [storedOrgId, setStoredOrgId] = useOrgId();
  const invitation = searchParams.get('invitation');
  const organization = searchParams.get('organization');
  const onRedirectCallback = (appState?: AppState, user?: User) => {
    if (mode === 'application') {
      sessionStorage.removeItem(ATTEMPTED_ORG_STORAGE_KEY);
      if (user?.org_id) {
        setStoredOrgId(user.org_id);
      }
    }
    navigate(appState?.returnTo || window.location.pathname, {
      replace: true,
    });
  };

  const orgId = resolveAuthOrganization({
    mode,
    explicitOrganization: explicitOrganizationFromSearchParams(searchParams),
    storedOrganization: storedOrgId,
  });

  const defaultAuthOptions =
    mode === 'signup'
      ? CONFIDENCE_SignupAuthOptions
      : CONFIDENCE_DefaultAuthOptions;
  const CurrentAuth0Context =
    mode === 'signup' ? SignupAuth0Context : Auth0Context;
  const skipRedirectCallback =
    mode !== 'signup' && window.location.pathname.startsWith('/signup');

  const authOptions = useMemo(
    () =>
      createAuthOptions({
        ...defaultAuthOptions,
        clientId,
        skipRedirectCallback,
        authorizationParams: {
          ...defaultAuthOptions.authorizationParams,
          organization: orgId,
        },
      }),
    [clientId, defaultAuthOptions, orgId, skipRedirectCallback],
  );

  return (
    <Auth0Provider
      {...authOptions}
      context={CurrentAuth0Context}
      onRedirectCallback={onRedirectCallback}
      authorizationParams={{
        ...authOptions.authorizationParams,
        redirect_uri: redirectUri ?? window.location.origin,
        ...(invitation && organization
          ? { invitation, organization }
          : { organization: orgId }),
      }}
    >
      <CurrentAuth0Context.Consumer>
        {value => (
          <>
            <FailedRedirectCallbackCleanup
              error={value.error}
              clearInvalidOrganization={mode === 'application'}
              clearAttemptedOrganization={mode === 'application'}
              handlesRedirectCallback={!skipRedirectCallback}
            />
            {mode === 'signup' ? (
              children
            ) : (
              <TokenWrapper context={value} onTokenReceived={onTokenReceived}>
                {children}
              </TokenWrapper>
            )}
          </>
        )}
      </CurrentAuth0Context.Consumer>
    </Auth0Provider>
  );
};

const logoutHandlers = new Set<() => void | Promise<void>>();

export const registerLogoutHandler = (
  handler: () => void | Promise<void>,
): (() => void) => {
  logoutHandlers.add(handler);
  return () => logoutHandlers.delete(handler);
};

const useAuthContext = (
  context: typeof Auth0Context,
  mode: AuthenticationMode,
) => {
  const {
    logout: auth0Logout,
    error: auth0Error,
    loginWithRedirect: auth0LoginWithRedirect,
    ...auth0
  } = useAuth0(context);
  const [storedOrgId, setOrgId] = useOrgId();
  const { orgName } = useContext(TokenClaimsContext);

  const error =
    auth0Error &&
    (getAuth0ErrorCode(auth0Error) === 'cancelled' ||
      isAuthCallbackReplayError(auth0Error))
      ? undefined
      : auth0Error;

  const loginWithRedirect: typeof auth0LoginWithRedirect = useCallback(
    async options => {
      if (mode === 'application') {
        rememberAttemptedOrganization(
          resolveAuthOrganization({
            mode,
            explicitOrganization:
              options?.authorizationParams?.organization ||
              explicitOrganizationFromSearchParams(
                new URLSearchParams(window.location.search),
              ),
            storedOrganization: storedOrgId,
          }),
        );
      }
      return auth0LoginWithRedirect(options);
    },
    [auth0LoginWithRedirect, mode, storedOrgId],
  );

  const logout: typeof auth0Logout = useCallback(
    async options => {
      if (mode === 'application') {
        setOrgId(null);
        sessionStorage.removeItem(ATTEMPTED_ORG_STORAGE_KEY);
        await Promise.allSettled(
          Array.from(logoutHandlers).map(handler => handler()),
        );
      }
      return auth0Logout(options);
    },
    [auth0Logout, mode, setOrgId],
  );

  return {
    ...auth0,
    loginWithRedirect,
    error,
    logout,
    orgName,
  };
};

export const useAuth = () => useAuthContext(Auth0Context, 'application');

export const useSignupAuth = () => useAuthContext(SignupAuth0Context, 'signup');
